import { useNavigate} from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Spinner from "../../Utils/spinner/loading";
import { useUploadVesselGeneralInfo } from "../utils/uploadgeneralvesselinfo";
import useFetchVesselClassifications from "../utils/useFetchVesselClassifications";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { ReactComponent as VatugIcon } from "../../Asset/black.svg";
import { HiX } from 'react-icons/hi';
import { HiArrowLeft } from 'react-icons/hi';
import "../create_vessels/createvessel.css";

const defaultForm = {
  numberOfBerths: "",
  deckSpace: "",
  deckStrength: "",
  classification: "", 
  yearOfBuilt: "",
  speed: "", 
  portOfRegistry: "",
  callSign: "", 
  officialNumber: "",
  imoNumber: "",
  mmsiNumber: "",
  lengthOverall: "",
  beamMoulded: "",
  draft: "",
  grossTonnage: "",
  cruisingSpeed: "",
  range: "",
  fuelCapacity: "",
  propulsion: "",
  generators: "",
  waterCapacity: "",
  builder: "",
  keelLaid: "",
  bollardPull: "",
  bhp: "",
  numberOfPassengers: "",
  trackable: null,
};

const VesselGeneralInformation = () => {
  const [formField, setFormField] = useState(defaultForm);
  const { mutate, isLoading, error: mutationError } = useUploadVesselGeneralInfo();
  const { data: vesselClassificationsData, isLoading: isFetchingVesselClassifications } = useFetchVesselClassifications();
  const vesselType = localStorage.getItem("selectedVesselType");
  const navigate = useNavigate();

  // Custom dropdown states
  const [isClassificationOpen, setIsClassificationOpen] = useState(false);
  const [isTrackableOpen, setIsTrackableOpen] = useState(false);
  
  const [classificationSearchTerm, setClassificationSearchTerm] = useState("");
  const [trackableSearchTerm, setTrackableSearchTerm] = useState("");
  
  // Refs for click outside handling
  const classificationRef = useRef(null);
  const trackableRef = useRef(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classificationRef.current && !classificationRef.current.contains(event.target)) {
        setIsClassificationOpen(false);
      }
      if (trackableRef.current && !trackableRef.current.contains(event.target)) {
        setIsTrackableOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle dropdowns
  const toggleClassificationDropdown = () => {
    setIsClassificationOpen(!isClassificationOpen);
    setIsTrackableOpen(false);
  };

  const toggleTrackableDropdown = () => {
    setIsTrackableOpen(!isTrackableOpen);
    setIsClassificationOpen(false);
  };

  // Filter options based on search terms
  const filteredClassifications = vesselClassificationsData?.data?.filter(classification => 
    classification.toLowerCase().includes(classificationSearchTerm.toLowerCase())
  ) || [];

  // Trackable options
  const trackableOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" }
  ];

  const filteredTrackableOptions = trackableOptions.filter(option => 
    option.label.toLowerCase().includes(trackableSearchTerm.toLowerCase())
  );

  // Handle classification selection
  const handleClassificationSelect = (classification) => {
    setFormField({
      ...formField,
      classification
    });
    setIsClassificationOpen(false);
    setClassificationSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsClassificationOpen(false);
    }, 0);
  };

  // Handle trackable selection
  const handleTrackableSelect = (value) => {
    setFormField({
      ...formField,
      trackable: value
    });
    setIsTrackableOpen(false);
    setTrackableSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsTrackableOpen(false);
    }, 0);
  };

  const {
    numberOfBerths,
    deckSpace,
    deckStrength,
    classification,
    yearOfBuilt,
    speed,
    portOfRegistry,
    callSign,
    officialNumber,
    imoNumber,
    mmsiNumber,
    lengthOverall,
    beamMoulded,
    draft,
    grossTonnage,
    cruisingSpeed,
    range,
    fuelCapacity,
    propulsion,
    generators,
    waterCapacity,
    builder,
    keelLaid,
    bollardPull,
    bhp,
    numberOfPassengers,
    trackable,
  } = formField;

  useEffect(() => {
    if (mutationError) {
      toast.error(mutationError.message || "An error occurred during submission. Please try again.");
    }
  }, [mutationError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormField({
      ...formField,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    for (let key in formField) {
      // Skip validation for `bollardPull` if the vessel type does not require it
      if ((vesselType !== "AHTS 001") && key === "bollardPull") {
        continue;
      }
      
      if (formField[key] === "") {
        toast.error("All fields are required. Please fill out the entire form.");
        return;
      }
    }

    const numericFields = [
      "numberOfBerths",
      "deckSpace",
      "deckStrength",
      "speed",
      "lengthOverall",
      "beamMoulded",
      "draft",
      "grossTonnage",
      "cruisingSpeed",
      "range",
      "fuelCapacity",
      "propulsion",
      "waterCapacity",
      "keelLaid",
      "bollardPull",
      "bhp",
      "numberOfPassengers",
    ];

    const formToSubmit = { ...formField };

    // Convert numeric fields to numbers
    numericFields.forEach((field) => {
      if (formToSubmit[field] !== "") {
        formToSubmit[field] = Number(formToSubmit[field]);
      }
    });

    // Remove `bollardPull` if vesselType is not "AHTS 001" or "SEV 001"
    if (vesselType !== "AHTS 001") {
      delete formToSubmit.bollardPull;
    }

    mutate(formToSubmit, {
      onError: (err) => {
        console.error(err);
      },
    });
  };

  // Helper function to get trackable label
  const getTrackableLabel = () => {
    if (trackable === null) return "Select Trackable";
    return trackable ? "Yes" : "No";
  };

  const handleGoBack = () => {
    navigate("/vessel_infomation");
  };

  return ( 
    <div className="create-vessel-container">
      <div className="create-vessel-header-bar">
        <div className="logo-container">
          <VatugIcon />
          <div className="logo-underline"></div>
        </div>
        <div className="title">Vessel Upload</div>
        <button className="close-btn" onClick={() => navigate("/dashboard/myvessels")}> <HiX /> </button>
      </div>

      <div className="create-vessel-card">
        <h2>Vessel General Information</h2>
        <div className="inputs">
          <label>
            Number of Berths
            <input type="text" name="numberOfBerths" value={numberOfBerths} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Deck Space
            <input type="text" name="deckSpace" value={deckSpace} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Deck Strength
            <input type="text" name="deckStrength" value={deckStrength} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Classification
            {isFetchingVesselClassifications ? (
              <Spinner />
            ) : (
              <div className="custom-select-container" ref={classificationRef}>
                <button 
                  type="button"
                  className="custom-select-button"
                  onClick={toggleClassificationDropdown}
                >
                  {classification || "Select Classification"}
                </button>
                {isClassificationOpen && (
                  <div className="custom-select-dropdown show">
                    <div className="custom-select-search">
                      <input
                        type="text"
                        placeholder="Search classification..."
                        value={classificationSearchTerm}
                        onChange={(e) => setClassificationSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {filteredClassifications.length > 0 ? (
                      filteredClassifications.map((classification, index) => (
                        <div
                          key={index}
                          className={`custom-select-option ${formField.classification === classification ? 'selected' : ''}`}
                          onClick={() => handleClassificationSelect(classification)}
                        >
                          {classification}
                        </div>
                      ))
                    ) : (
                      <div className="custom-select-option">No classifications found</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </label>
          <label>
            Year of Built
            <input type="text" name="yearOfBuilt" value={yearOfBuilt} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Speed
            <input type="text" name="speed" value={speed} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Port of Registry
            <input type="text" name="portOfRegistry" value={portOfRegistry} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Call Sign
            <input type="text" name="callSign" value={callSign} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Official Number
            <input type="text" name="officialNumber" value={officialNumber} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            IMO Number
            <input type="text" name="imoNumber" value={imoNumber} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            MMSI Number
            <input type="text" name="mmsiNumber" value={mmsiNumber} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Length Overall
            <input type="text" name="lengthOverall" value={lengthOverall} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Beam Moulded
            <input type="text" name="beamMoulded" value={beamMoulded} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Draft
            <input type="text" name="draft" value={draft} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Gross Tonnage
            <input type="text" name="grossTonnage" value={grossTonnage} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Cruising Speed
            <input type="text" name="cruisingSpeed" value={cruisingSpeed} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Range
            <input type="text" name="range" value={range} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Fuel Capacity
            <input type="text" name="fuelCapacity" value={fuelCapacity} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Propulsion
            <input type="text" name="propulsion" value={propulsion} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Generators
            <input type="text" name="generators" value={generators} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Water Capacity
            <input type="text" name="waterCapacity" value={waterCapacity} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Builder
            <input type="text" name="builder" value={builder} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Keel Laid
            <input type="text" name="keelLaid" value={keelLaid} onChange={handleChange} placeholder="Enter" />
          </label>
          {
            (vesselType === "AHTS 001") && (
              <label>
                Bollard Pull
                <input type="text" name="bollardPull" value={bollardPull} onChange={handleChange} placeholder="Enter" />
              </label>
            )
          }
          <label>
            BHP
            <input type="text" name="bhp" value={bhp} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Number of Passengers
            <input type="text" name="numberOfPassengers" value={numberOfPassengers} onChange={handleChange} placeholder="Enter" />
          </label>
          <label>
            Trackable
            <div className="custom-select-container" ref={trackableRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={toggleTrackableDropdown}
              >
                {getTrackableLabel()}
              </button>
              {isTrackableOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={trackableSearchTerm}
                      onChange={(e) => setTrackableSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredTrackableOptions.length > 0 ? (
                    filteredTrackableOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`custom-select-option ${trackable === option.value ? 'selected' : ''}`}
                        onClick={() => handleTrackableSelect(option.value)}
                      >
                        {option.label}
                      </div>
                    ))
                  ) : (
                    <div className="custom-select-option">No options found</div>
                  )}
                </div>
              )}
            </div>
          </label>
        </div>

        <div className="button-row">
          <button className="back-btn" onClick={handleGoBack}>
            <HiArrowLeft style={{marginRight: 6}}/> Go Back
          </button>
          <button 
            className="next-btn" 
            onClick={handleSubmit} 
            disabled={isLoading}
          >
            {isLoading ? <Spinner /> : "Next"}
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default VesselGeneralInformation;


